/**
 * MySubscriptionView - Subscription management page
 * Shows current plan, payment history, and management options
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useTranslation } from "../translations";
import { useAuth } from "../App";
import { CheckCircle, Clock, CreditCard, Shield, AlertTriangle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MySubscriptionView = ({ onNavigateToPlans }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();

  useEffect(() => {
    fetchDetails();
  }, []);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/my-subscription/details`);
      setData(res.data);
    } catch (err) {
      console.error("Error fetching subscription:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t('cancelSubscriptionConfirm') || 'Tem certeza que deseja cancelar? Seu plano continuará ativo até o vencimento.')) return;
    
    setCanceling(true);
    try {
      await axios.post(`${API}/my-subscription/cancel`);
      alert(t('subscriptionCancelled') || 'Assinatura cancelada. Permanecerá ativa até o vencimento.');
      fetchDetails();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setCanceling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!data) return null;

  const sub = data.subscription;
  const isFreePlan = sub?.is_free;
  const isPremium = sub?.plan_id === 'premium_lifetime';
  const expiresAt = sub?.expires_at ? new Date(sub.expires_at) : null;
  const isExpiringSoon = expiresAt && (expiresAt - new Date()) < 7 * 24 * 60 * 60 * 1000;

  return (
    <div className="space-y-6" data-testid="my-subscription-view">
      {/* Current Plan Card */}
      <Card className={`${isPremium ? 'ring-2 ring-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-50' : isFreePlan ? 'bg-gray-50' : 'ring-2 ring-green-400 bg-gradient-to-r from-green-50 to-emerald-50'}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2" data-testid="current-plan-title">
              <Shield className="w-5 h-5" />
              {t('mySubscription') || 'Minha Assinatura'}
            </CardTitle>
            <Badge className={`${sub?.status === 'active' ? 'bg-green-500' : 'bg-gray-400'} text-white`} data-testid="plan-status-badge">
              {sub?.status === 'active' ? (t('active') || 'Ativo') : sub?.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900" data-testid="plan-name">
                {sub?.plan_name || sub?.plan_id}
              </h3>
              {isPremium && (
                <p className="text-amber-700 font-medium mt-1">
                  {t('premiumLifetime') || 'Acesso vitalício a todos os recursos'}
                </p>
              )}
              {isFreePlan && !isPremium && (
                <p className="text-gray-600 mt-1">
                  {t('freePlanDesc') || 'Plano gratuito com recursos limitados'}
                </p>
              )}
              {!isFreePlan && !isPremium && expiresAt && (
                <div className="mt-2">
                  <p className="text-gray-600 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {t('expiresAt') || 'Expira em'}: <strong>{expiresAt.toLocaleDateString('pt-BR')}</strong>
                  </p>
                  {isExpiringSoon && (
                    <p className="text-orange-600 flex items-center gap-1 mt-1">
                      <AlertTriangle className="w-4 h-4" />
                      {t('expiringSoon') || 'Expira em breve! Renove para não perder acesso.'}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 justify-center">
              {isFreePlan && !isPremium && (
                <Button
                  data-testid="upgrade-plan-btn"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={onNavigateToPlans}
                >
                  {t('upgradePlan') || 'Fazer Upgrade'}
                </Button>
              )}
              {!isFreePlan && !isPremium && (
                <>
                  <Button
                    data-testid="change-plan-btn"
                    variant="outline"
                    onClick={onNavigateToPlans}
                  >
                    {t('changePlan') || 'Trocar Plano'}
                  </Button>
                  <Button
                    data-testid="cancel-subscription-btn"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={handleCancel}
                    disabled={canceling}
                  >
                    {canceling ? (t('processing') || 'Processando...') : (t('cancelSubscription') || 'Cancelar Assinatura')}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Plan Limits */}
          {sub?.limits && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="font-semibold text-gray-700 mb-3">{t('planResources') || 'Recursos do Plano'}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {sub.limits.properties !== undefined && (
                  <div className="bg-white rounded-lg p-3 text-center border">
                    <div className="text-2xl font-bold text-blue-600">
                      {sub.limits.properties === -1 ? '∞' : sub.limits.properties}
                    </div>
                    <div className="text-xs text-gray-500">{t('properties') || 'Propriedades'}</div>
                  </div>
                )}
                {sub.limits.workers !== undefined && (
                  <div className="bg-white rounded-lg p-3 text-center border">
                    <div className="text-2xl font-bold text-green-600">
                      {sub.limits.workers === -1 ? '∞' : sub.limits.workers}
                    </div>
                    <div className="text-xs text-gray-500">{t('workers') || 'Trabalhadores'}</div>
                  </div>
                )}
                {sub.limits.calendar_access !== undefined && (
                  <div className="bg-white rounded-lg p-3 text-center border">
                    <div className="text-2xl font-bold">
                      {sub.limits.calendar_access ? <CheckCircle className="w-6 h-6 text-green-500 mx-auto" /> : '—'}
                    </div>
                    <div className="text-xs text-gray-500">{t('calendar') || 'Calendário'}</div>
                  </div>
                )}
                {sub.limits.give_evaluations !== undefined && (
                  <div className="bg-white rounded-lg p-3 text-center border">
                    <div className="text-2xl font-bold">
                      {sub.limits.give_evaluations ? <CheckCircle className="w-6 h-6 text-green-500 mx-auto" /> : '—'}
                    </div>
                    <div className="text-xs text-gray-500">{t('evaluations') || 'Avaliações'}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment History */}
      {data.payment_history && data.payment_history.length > 0 && (
        <Card data-testid="payment-history-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              {t('paymentHistory') || 'Histórico de Pagamentos'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.payment_history.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">
                      {payment.plan_id ? `${t('plan')} ${payment.plan_id}` : payment.item_type}
                      {payment.billing_cycle && ` (${payment.billing_cycle})`}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(payment.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">
                      {payment.currency === 'usd' ? '$' : 'R$'} {payment.amount?.toFixed(2)}
                    </p>
                    <Badge className="bg-green-100 text-green-700">
                      {t('paid') || 'Pago'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Purchased Items */}
      {data.purchased_items && data.purchased_items.length > 0 && (
        <Card data-testid="purchased-items-card">
          <CardHeader>
            <CardTitle>{t('activeItems') || 'Itens Ativos'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.purchased_items.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium">{item.item_type}</span>
                  <span className="text-sm text-gray-500">
                    {t('expiresAt') || 'Expira'}: {new Date(item.expires_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upgrade CTA for free users */}
      {isFreePlan && !isPremium && (
        <Card className="bg-gradient-to-r from-blue-500 to-blue-700 text-white" data-testid="upgrade-cta">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('upgradeTitle') || 'Desbloqueie todo o potencial!'}</h3>
            <p className="mb-4 opacity-90">
              {t('upgradeDesc') || 'Faça upgrade para acessar calendário completo, avaliações, gestão de equipe e muito mais.'}
            </p>
            <Button
              data-testid="upgrade-cta-btn"
              className="bg-white text-blue-700 hover:bg-blue-50"
              onClick={onNavigateToPlans}
            >
              {t('seeAllPlans') || 'Ver Todos os Planos'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MySubscriptionView;
